// Copyright ©️ 2026 eVolve MEP, LLC

import type { BendCreatorCreateRequest, Facility, Project, StrongOmit, WorkOrderPaginated } from '@evolvemep/api-ts';
import { Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';

import { useFormModal } from 'components/Mantine/Modals/FormModal';
import type { RequireAtLeastOne } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { WorkOrderSearchableSelectForFabrication } from 'modules/Shop/EvolveFabrication/WorkOrderSearchableSelectForFabrication';

type BendFormMachineElementData = {
  workOrder: WorkOrderPaginated | null;
  location: string;
  level: string;
  runId: string;
  description: string;
};

export type BendFormBendData = StrongOmit<
  BendCreatorCreateRequest,
  'workOrderId' | 'location' | 'level' | 'runId' | 'description'
>;

type Props = {
  elementValues?: Partial<StrongOmit<BendFormMachineElementData, 'workOrder'>>;
  onSubmit: (v: BendFormMachineElementData) => Promise<void>;
} & (
  | {
      isEditing: true;
      workOrder?: never;
      selectedProjects?: never;
      selectedFacilities?: never;
    }
  | ({
      isEditing?: never;
      selectedFacilities?: Facility[];
    } & RequireAtLeastOne<{
      workOrder?: WorkOrderPaginated;
      selectedProjects?: Project[];
    }>)
);

export const BendElementDetailsForm = ({
  elementValues,
  workOrder,
  isEditing,
  selectedProjects,
  selectedFacilities,
  onSubmit: parentOnSubmit,
}: Props) => {
  const form = useForm<BendFormMachineElementData>({
    initialValues: {
      location: elementValues?.location ?? '',
      level: elementValues?.level ?? '',
      runId: elementValues?.runId ?? '',
      description: elementValues?.description ?? '',
      workOrder: workOrder ?? null,
    },
    validate: {
      workOrder: (v) => {
        if (isEditing) return false;
        return isNil(v);
      },
    },
  });

  useFormModal(form, parentOnSubmit);

  return (
    <>
      {!isEditing && isNil(workOrder) && (
        <WorkOrderSearchableSelectForFabrication
          // `props.selectedProjects` will always be defined if `props.workOrder` is nil,
          // but TypeScript can't tell
          selectedProjects={selectedProjects ?? []}
          selectedFacilities={selectedFacilities}
          alwaysShowWhenLoading
          {...form.getInputProps('workOrder')}
        />
      )}
      <TextInput label="Location" {...form.getInputProps('location')} />
      <TextInput label="Level" {...form.getInputProps('level')} />
      <TextInput label="Run ID" {...form.getInputProps('runId')} />
      <Textarea label="Description / Comment" {...form.getInputProps('description')} minRows={4} />
    </>
  );
};
